import { useEffect, useRef, useState } from 'react';
import { api, tokens } from './api/client';
import type { Device } from './api/types';

interface PositionMessage {
  deviceId: number;
  position: {
    id: number;
    deviceTime: string;
    latitude: number;
    longitude: number;
    speedKph: number;
    course: number;
    valid: boolean;
  };
}

interface StateMessage {
  deviceId: number;
  ignition?: boolean | null;
  blocked?: boolean | null;
  armed?: boolean | null;
  batteryLevel?: number | null;
  gsmSignal?: number | null;
  lastSeenAt?: string | null;
}

interface PresenceMessage {
  deviceId: number;
  online: boolean;
}

/**
 * Device list plus a WebSocket that keeps it current.
 *
 * The list is fetched once over HTTP, then patched in place by live messages.
 * The socket reconnects with backoff and re-fetches on reconnect, so anything
 * missed while disconnected is not left stale on screen.
 */
/** A vehicle the server says has stopped reporting. */
export interface OfflineAlert {
  deviceId: number;
  name: string;
  at: number;
}

export function useLiveFeed() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  /**
   * Offline alerts come from the server, not from a second rule in here.
   *
   * The browser already watches speed to decide "started" and "stopped", but
   * "stopped reporting" is decided by a sweep on the server — and duplicating
   * that rule client-side would give two answers that drift apart, on a
   * question where the phone and the dashboard should never disagree.
   */
  const [offline, setOffline] = useState<OfflineAlert[]>([]);

  const socketRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  const refresh = async () => {
    try {
      setDevices(await api.devices());
      setError(null);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    void refresh();

    const connect = () => {
      if (cancelled) return;
      const token = tokens.access;
      if (!token) return;

      const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
      const socket = new WebSocket(
        `${protocol}://${location.host}/ws?token=${encodeURIComponent(token)}`,
      );
      socketRef.current = socket;

      socket.onopen = () => {
        if (cancelled) return;
        setConnected(true);
        // Whatever changed while we were away is not in our state.
        if (attemptRef.current > 0) void refresh();
        attemptRef.current = 0;
      };

      socket.onmessage = (event) => {
        let parsed: { type: string; data: unknown };
        try {
          parsed = JSON.parse(event.data);
        } catch {
          return;
        }

        if (parsed.type === 'position') {
          const { deviceId, position } = parsed.data as PositionMessage;
          setDevices((current) =>
            current.map((device) =>
              device.id === deviceId
                ? {
                    ...device,
                    online: true,
                    lastSeenAt: new Date().toISOString(),
                    position: {
                      ...position,
                      googleMapsUrl: `https://www.google.com/maps?q=${position.latitude},${position.longitude}`,
                    },
                  }
                : device,
            ),
          );
        }

        if (parsed.type === 'event') {
          const update = parsed.data as {
            deviceId: number;
            type: string;
            payload?: { name?: string };
          };
          if (update.type === 'device.offline') {
            setOffline((current) =>
              [
                ...current,
                {
                  deviceId: update.deviceId,
                  name: update.payload?.name ?? '',
                  at: Date.now(),
                },
              ].slice(-20),
            );
            // Reflect it immediately rather than waiting for the next poll:
            // the row should go grey the moment the alert appears.
            setDevices((current) =>
              current.map((device) =>
                device.id === update.deviceId ? { ...device, online: false } : device,
              ),
            );
          }
        }

        if (parsed.type === 'state') {
          const update = parsed.data as StateMessage;
          setDevices((current) =>
            current.map((device) =>
              device.id === update.deviceId
                ? {
                    ...device,
                    online: true,
                    lastSeenAt: update.lastSeenAt ?? device.lastSeenAt,
                    status: {
                      // Null means the device did not report it, so keep what we had.
                      ignition: update.ignition ?? device.status.ignition,
                      blocked: update.blocked ?? device.status.blocked,
                      armed: update.armed ?? device.status.armed,
                      batteryLevel: update.batteryLevel ?? device.status.batteryLevel,
                      gsmSignal: update.gsmSignal ?? device.status.gsmSignal,
                      powerVoltage: device.status.powerVoltage,
                    },
                  }
                : device,
            ),
          );
        }

        if (parsed.type === 'presence') {
          const { deviceId, online } = parsed.data as PresenceMessage;
          setDevices((current) =>
            current.map((device) => (device.id === deviceId ? { ...device, online } : device)),
          );
        }
      };

      socket.onclose = () => {
        if (cancelled) return;
        setConnected(false);
        // Back off to a 30s ceiling so a restarting server is not hammered.
        const delay = Math.min(1000 * 2 ** attemptRef.current, 30_000);
        attemptRef.current += 1;
        timerRef.current = window.setTimeout(connect, delay);
      };

      socket.onerror = () => socket.close();
    };

    connect();

    return () => {
      cancelled = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
      socketRef.current?.close();
    };
  }, []);

  return {
    devices,
    loading,
    error,
    connected,
    refresh,
    offline,
    clearOffline: () => setOffline([]),
  };
}
